import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Calculator,
  Save,
  FileText,
  User,
  Euro,
  Percent,
  Users,
  Receipt,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

import { payrollAPI, employeesAPI } from "@/lib/api";
import { exportPayslip } from "@/services/pdfExport.service";

export default function PayrollCalculator() {
  const { user } = useAuth();
  const { toast, success: showSuccess, error: showError } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    employe_id: "",
    periode_debut: "",
    periode_fin: "",
    salaire_base: "",
    heures_travaillees: 173.33, // Moroccan standard: 173.33 hours per month (40h/semaine)
    heures_supplementaires: 0,
    taux_heures_sup: 1.25,
    primes: 0,
    nombre_personnes_charge: 0,
    autres_deductions: 0,
  });

  const [calculations, setCalculations] = useState({
    salaire_brut: 0,
    cotisations_cnss: 0, // 5.5% du salaire brut
    cotisations_amo: 0, // 2% du salaire brut
    cotisations_retraite: 0, // 1% du salaire brut
    cotisations_sociales: 0, // Total des cotisations
    frais_professionnels: 0, // 20% plafonné à 2500 MAD
    salaire_net_imposable: 0, // SNI = SB - Cotisations - Frais Pro
    ir_brut: 0, // Impôt sur le revenu brut
    deduction_personnes: 0, // 30 MAD par personne à charge
    ir_net: 0, // IR final après déductions
    salaire_net: 0, // Salaire net final
  });

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "rh") {
      loadEmployees();
    }
  }, []);

  useEffect(() => {
    calculatePayroll();
  }, [formData]);

  const loadEmployees = async () => {
    try {
      const response = await employeesAPI.getAll({ limit: 1000 });
      setEmployees(response.data?.data?.employees || []);
      console.log(response);
    } catch (error) {
      console.error("Error loading employees:", error);
      setEmployees([]); // Ensure employees is always an array
      showError("Erreur lors du chargement des employés");
    }
  };

  const calculatePayroll = () => {
    const salaireBase = parseFloat(formData.salaire_base) || 0;
    const heuresSupplementaires =
      parseFloat(formData.heures_supplementaires) || 0;
    const tauxHeuresSup = parseFloat(formData.taux_heures_sup) || 1.25;
    const primes = parseFloat(formData.primes) || 0;
    const nombrePersonnesCharge =
      parseInt(formData.nombre_personnes_charge) || 0;
    const autresDeductions = parseFloat(formData.autres_deductions) || 0;
    const heuresStandard = parseFloat(formData.heures_travaillees) || 173.33;

    // 1. Calculer le salaire brut selon la formule exacte
    const tauxHoraire = salaireBase / heuresStandard;
    const paieHeuresSupplementaires =
      heuresSupplementaires * tauxHoraire * tauxHeuresSup;
    const salaireBrut = salaireBase + paieHeuresSupplementaires + primes;

    // 2. Calculer les frais professionnels (20% plafonné à 2500 MAD)
    const fraisProfessionnels = Math.min(salaireBrut * 0.2, 2500);

    // 3. Calculer les cotisations sociales
    const baseCNSS = Math.min(salaireBrut, 6000); // Plafond CNSS
    const cotisationsCNSS = baseCNSS * 0.0429; // 4.29%
    const cotisationsAMO = salaireBrut * 0.0226; // 2.26%
    const cotisationsRetraite = 0; // 0% (ajustable)
    const totalCotisations =
      cotisationsCNSS + cotisationsAMO + cotisationsRetraite;

    // 4. Calculer le salaire net imposable
    const salaireNetImposable =
      salaireBrut - fraisProfessionnels - totalCotisations;

    // 5. Calculer l'IR selon le barème marocain exact
    const irBrut = calculateMoroccanIncomeTax(
      salaireNetImposable,
      nombrePersonnesCharge
    );

    // 6. Calculer le salaire net final
    const salaireNet =
      salaireBrut - totalCotisations - irBrut - autresDeductions;

    setCalculations({
      salaire_brut: salaireBrut,
      cotisations_cnss: cotisationsCNSS,
      cotisations_amo: cotisationsAMO,
      cotisations_retraite: cotisationsRetraite,
      cotisations_sociales: totalCotisations,
      frais_professionnels: fraisProfessionnels,
      salaire_net_imposable: salaireNetImposable,
      ir_brut: irBrut,
      deduction_personnes: Math.min(nombrePersonnesCharge, 6) * 30,
      ir_net: irBrut,
      salaire_net: salaireNet,
    });
  };

  // Calcul de l'IR selon le barème marocain exact
  const calculateMoroccanIncomeTax = (
    salaireNetImposable,
    nombrePersonnesCharge
  ) => {
    const revenuAnnuel = salaireNetImposable * 12;
    const deductionPersonnes = Math.min(nombrePersonnesCharge, 6) * 360;
    const revenuImposable = Math.max(revenuAnnuel - deductionPersonnes, 0);

    let irAnnuel = 0;

    // Barème progressif marocain 2024
    if (revenuImposable <= 30000) {
      irAnnuel = 0;
    } else if (revenuImposable <= 50000) {
      // Seulement la partie au-dessus de 30000 est imposée à 10%
      const montantTranche = revenuImposable - 30000;
      irAnnuel = montantTranche * 0.1;
    } else if (revenuImposable <= 60000) {
      // 20000 imposés à 10% + le reste à 20%
      const montantTranche1 = 20000; // 50000 - 30000
      const montantTranche2 = revenuImposable - 50000;
      irAnnuel = montantTranche1 * 0.1 + montantTranche2 * 0.2;
    } else if (revenuImposable <= 80000) {
      // 20000 à 10% + 10000 à 20% + le reste à 30%
      const montantTranche1 = 20000; // 50000 - 30000
      const montantTranche2 = 10000; // 60000 - 50000
      const montantTranche3 = revenuImposable - 60000;
      irAnnuel =
        montantTranche1 * 0.1 + montantTranche2 * 0.2 + montantTranche3 * 0.3;
    } else if (revenuImposable <= 180000) {
      // 20000 à 10% + 10000 à 20% + 20000 à 30% + le reste à 34%
      const montantTranche1 = 20000; // 50000 - 30000
      const montantTranche2 = 10000; // 60000 - 50000
      const montantTranche3 = 20000; // 80000 - 60000
      const montantTranche4 = revenuImposable - 80000;
      irAnnuel =
        montantTranche1 * 0.1 +
        montantTranche2 * 0.2 +
        montantTranche3 * 0.3 +
        montantTranche4 * 0.34;
    } else {
      // 20000 à 10% + 10000 à 20% + 20000 à 30% + 100000 à 34% + le reste à 38%
      const montantTranche1 = 20000; // 50000 - 30000
      const montantTranche2 = 10000; // 60000 - 50000
      const montantTranche3 = 20000; // 80000 - 60000
      const montantTranche4 = 100000; // 180000 - 80000
      const montantTranche5 = revenuImposable - 180000;
      irAnnuel =
        montantTranche1 * 0.1 +
        montantTranche2 * 0.2 +
        montantTranche3 * 0.3 +
        montantTranche4 * 0.34 +
        montantTranche5 * 0.38;
    }

    return Math.max(0, irAnnuel / 12);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGeneratePayslip = async () => {
    if (
      !formData.employe_id ||
      !formData.periode_debut ||
      !formData.periode_fin
    ) {
      showError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      setLoading(true);

      const payrollData = {
        employe_id: formData.employe_id,
        periode_debut: formData.periode_debut,
        periode_fin: formData.periode_fin,
        salaire_base: parseFloat(formData.salaire_base),
        heures_travaillees: parseFloat(formData.heures_travaillees),
        heures_supplementaires: parseFloat(formData.heures_supplementaires),
        taux_heures_sup: parseFloat(formData.taux_heures_sup),
        primes: parseFloat(formData.primes),
        nombre_personnes_charge: parseInt(formData.nombre_personnes_charge),
        autres_deductions: parseFloat(formData.autres_deductions),
        // Calculated fields
        salaire_brut: calculations.salaire_brut,
        cotisations_cnss: calculations.cotisations_cnss,
        cotisations_amo: calculations.cotisations_amo,
        cotisations_retraite: calculations.cotisations_retraite,
        cotisations_sociales: calculations.cotisations_sociales,
        frais_professionnels: calculations.frais_professionnels,
        salaire_net_imposable: calculations.salaire_net_imposable,
        ir_brut: calculations.ir_brut,
        deduction_personnes: calculations.deduction_personnes,
        ir_net: calculations.ir_net,
        salaire_net: calculations.salaire_net,
      };

      // Create payslip in database
      const response = await payrollAPI.calculatePayroll(payrollData);
      const payslip = response.data.data;

      // Generate and download PDF
      await exportPayslip(payslip);

      showSuccess(
        "Bulletin de paie marocain généré et téléchargé avec succès !"
      );

      // Reset form
      setFormData({
        employe_id: "",
        periode_debut: "",
        periode_fin: "",
        salaire_base: "",
        heures_travaillees: 151.67,
        heures_supplementaires: 0,
        taux_heures_sup: 1.25,
        primes: 0,
        nombre_personnes_charge: 0,
        autres_deductions: 0,
      });
    } catch (error) {
      console.error("Error generating payslip:", error);
      showError("Erreur lors de la génération du bulletin de paie");
    } finally {
      setLoading(false);
    }
  };

  const canCalculatePayroll = user?.role === "admin" || user?.role === "rh";

  if (!canCalculatePayroll) {
    return (
      <div className="text-center py-8">
        <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">
          Accès refusé. Seuls les administrateurs et RH peuvent gérer la paie.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Calculator className="h-8 w-8 text-blue-600" />
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Calculateur de Paie Marocain
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Calculez automatiquement les salaires selon la réglementation
            marocaine
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="space-y-6">
          {/* Employee Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Employé</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="employe_id">Sélectionner un employé</Label>
                <Select
                  value={formData.employe_id}
                  onValueChange={(value) => {
                    handleInputChange("employe_id", value);
                    // Auto-fill salary if available
                    const employee = Array.isArray(employees)
                      ? employees.find((e) => e.id === value)
                      : null;
                    if (employee?.salaire) {
                      handleInputChange("salaire_base", employee.salaire);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un employé" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(employees) &&
                      employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.prenom} {employee.nom} -{" "}
                          {employee.departement?.nom}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="periode_debut">Début de période</Label>
                  <Input
                    id="periode_debut"
                    type="date"
                    value={formData.periode_debut}
                    onChange={(e) =>
                      handleInputChange("periode_debut", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="periode_fin">Fin de période</Label>
                  <Input
                    id="periode_fin"
                    type="date"
                    value={formData.periode_fin}
                    onChange={(e) =>
                      handleInputChange("periode_fin", e.target.value)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salary Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Euro className="h-5 w-5" />
                <span>Détails du Salaire</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="salaire_base">Salaire de Base (MAD)</Label>
                <Input
                  id="salaire_base"
                  type="number"
                  step="0.01"
                  value={formData.salaire_base}
                  onChange={(e) =>
                    handleInputChange("salaire_base", e.target.value)
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="heures_travaillees">Heures Travaillées</Label>
                  <Input
                    id="heures_travaillees"
                    type="number"
                    step="0.01"
                    value={formData.heures_travaillees}
                    onChange={(e) =>
                      handleInputChange("heures_travaillees", e.target.value)
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Standard: 173.33h/mois (40h/semaine)
                  </p>
                </div>
                <div>
                  <Label htmlFor="heures_supplementaires">
                    Heures Supplémentaires
                  </Label>
                  <Input
                    id="heures_supplementaires"
                    type="number"
                    step="0.5"
                    value={formData.heures_supplementaires}
                    onChange={(e) =>
                      handleInputChange(
                        "heures_supplementaires",
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="taux_heures_sup">Taux Heures Sup.</Label>
                  <Input
                    id="taux_heures_sup"
                    type="number"
                    step="0.01"
                    value={formData.taux_heures_sup}
                    onChange={(e) =>
                      handleInputChange("taux_heures_sup", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="primes">Primes (MAD)</Label>
                  <Input
                    id="primes"
                    type="number"
                    step="0.01"
                    value={formData.primes}
                    onChange={(e) =>
                      handleInputChange("primes", e.target.value)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Moroccan Specific Fields */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Receipt className="h-5 w-5" />
                <span>Paramètres Marocains</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nombre_personnes_charge">
                  <Users className="h-4 w-4 inline mr-2" />
                  Nombre de Personnes à Charge
                </Label>
                <Input
                  id="nombre_personnes_charge"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.nombre_personnes_charge}
                  onChange={(e) =>
                    handleInputChange("nombre_personnes_charge", e.target.value)
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Déduction de 30 MAD par personne (max 10)
                </p>
              </div>

              <div>
                <Label htmlFor="autres_deductions">
                  Autres Déductions (MAD)
                </Label>
                <Input
                  id="autres_deductions"
                  type="number"
                  step="0.01"
                  value={formData.autres_deductions}
                  onChange={(e) =>
                    handleInputChange("autres_deductions", e.target.value)
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  Déductions supplémentaires (assurance, prêts, etc.)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calculations */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Calculs Marocains</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Gross Salary Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Salaire de Base</span>
                  <span>
                    {parseFloat(formData.salaire_base || 0).toLocaleString(
                      "fr-MA",
                      { style: "currency", currency: "MAD" }
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Heures Supplémentaires</span>
                  <span>
                    {(
                      parseFloat(formData.heures_supplementaires || 0) *
                      (parseFloat(formData.salaire_base || 0) / 173.33) *
                      parseFloat(formData.taux_heures_sup || 1.25)
                    ).toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Primes</span>
                  <span>
                    {parseFloat(formData.primes || 0).toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Salaire Brut</span>
                  <span>
                    {calculations.salaire_brut.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Moroccan Social Contributions */}
              <div className="space-y-2">
                <h4 className="font-medium">Cotisations Sociales Marocaines</h4>
                <div className="flex justify-between text-sm">
                  <span>CNSS (5.5%)</span>
                  <span className="text-red-600">
                    -
                    {calculations.cotisations_cnss.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>AMO (2%)</span>
                  <span className="text-red-600">
                    -
                    {calculations.cotisations_amo.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Retraite (1%)</span>
                  <span className="text-red-600">
                    -
                    {calculations.cotisations_retraite.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium text-red-600">
                  <span>Total Cotisations</span>
                  <span>
                    -
                    {calculations.cotisations_sociales.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Professional Expenses and Deductions */}
              <div className="space-y-2">
                <h4 className="font-medium">Déductions et Frais</h4>
                <div className="flex justify-between text-sm">
                  <span>Frais Professionnels (20%)</span>
                  <span className="text-red-600">
                    -
                    {calculations.frais_professionnels.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Autres Déductions</span>
                  <span className="text-red-600">
                    -
                    {parseFloat(formData.autres_deductions || 0).toLocaleString(
                      "fr-MA",
                      {
                        style: "currency",
                        currency: "MAD",
                      }
                    )}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium">
                  <span>Salaire Net Imposable</span>
                  <span>
                    {calculations.salaire_net_imposable.toLocaleString(
                      "fr-MA",
                      {
                        style: "currency",
                        currency: "MAD",
                      }
                    )}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Moroccan Income Tax */}
              <div className="space-y-2">
                <h4 className="font-medium">Impôt sur le Revenu (IR)</h4>
                <div className="flex justify-between text-sm">
                  <span>IR Brut</span>
                  <span className="text-red-600">
                    -
                    {calculations.ir_brut.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Déduction Personnes à Charge</span>
                  <span className="text-green-600">
                    +
                    {calculations.deduction_personnes.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium text-red-600">
                  <span>IR Net</span>
                  <span>
                    -
                    {calculations.ir_net.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Final Net Salary */}
              <div className="space-y-2">
                <div className="flex justify-between font-bold text-lg text-green-600">
                  <span>Salaire Net Final</span>
                  <span>
                    {calculations.salaire_net.toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  onClick={handleGeneratePayslip}
                  disabled={loading || !formData.employe_id}
                  className="flex-1"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Chargement...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Save className="h-4 w-4" />
                      <span>Générer Bulletin de Paie</span>
                    </div>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
